import { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { useAppStore } from '@/store';
import { isOutOfDomain, rgbMinFromInkMax } from '@/engine/domain';
import type { WorkingDomain } from '@/types';
import { DEFAULT_WORKING_DOMAIN } from '@/types';

// =============================================================================
// Slider row
// =============================================================================

function SliderRow({
  label, value, min, max, step, rgbMin, onChange,
}: {
  label: string; value: number; min: number; max: number; step: number;
  rgbMin?: number; onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-3 text-xs">
      <span className="w-28 text-slate-300 shrink-0">{label}</span>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="flex-1 accent-sky-400"
      />
      <span className="w-10 text-right font-mono text-sky-300">{value.toFixed(2)}</span>
      {rgbMin !== undefined && (
        <span className="w-16 text-right font-mono text-slate-500">
          RGB≥{rgbMin}
        </span>
      )}
    </div>
  );
}

// =============================================================================
// Domain scatter (a* vs b* or ink vs L*)
// =============================================================================

type ScatterMode = 'ab' | 'Lv';

function DomainScatter({
  domain,
}: {
  domain: WorkingDomain;
}) {
  const patches    = useAppStore(s => s.session.patches)  ?? [];
  const diagnostics = useAppStore(s => s.session.diagnostics);
  const flaggedSet  = new Set(diagnostics?.flaggedPatchIds ?? []);

  const ref = useRef<SVGSVGElement>(null);
  const [mode, setMode] = useState<ScatterMode>('ab');

  useEffect(() => {
    if (!ref.current || !patches.length) return;
    const W = 480, H = 300, Mg = { top: 16, right: 24, bottom: 40, left: 44 };
    const iw = W - Mg.left - Mg.right;
    const ih = H - Mg.top  - Mg.bottom;

    const svg = d3.select(ref.current);
    svg.selectAll('*').remove();
    const g = svg.append('g').attr('transform', `translate(${Mg.left},${Mg.top})`);

    const xScale = mode === 'ab'
      ? d3.scaleLinear().domain([-70, 70]).nice().range([0, iw])
      : d3.scaleLinear().domain([0, 1]).range([0, iw]);
    const yScale = mode === 'ab'
      ? d3.scaleLinear().domain([-70, 70]).nice().range([ih, 0])
      : d3.scaleLinear().domain([0, 100]).nice().range([ih, 0]);

    g.append('g').attr('transform',`translate(0,${ih})`)
      .call(d3.axisBottom(xScale).ticks(6))
      .selectAll('text,line,path').attr('stroke','#64748b').attr('fill','#64748b');
    g.append('g').call(d3.axisLeft(yScale).ticks(5))
      .selectAll('text,line,path').attr('stroke','#64748b').attr('fill','#64748b');

    if (mode === 'ab') {
      g.append('line').attr('x1',xScale(0)).attr('x2',xScale(0))
        .attr('y1',0).attr('y2',ih).attr('stroke','#334155').attr('stroke-dasharray','3,3');
      g.append('line').attr('x1',0).attr('x2',iw)
        .attr('y1',yScale(0)).attr('y2',yScale(0)).attr('stroke','#334155').attr('stroke-dasharray','3,3');
    }

    g.append('text').attr('x',iw/2).attr('y',ih+32)
      .attr('text-anchor','middle').attr('fill','#64748b').attr('font-size',10)
      .text(mode === 'ab' ? 'a*' : 'Ink level (0=none, 1=full)');
    g.append('text').attr('transform','rotate(-90)').attr('x',-ih/2).attr('y',-32)
      .attr('text-anchor','middle').attr('fill','#64748b').attr('font-size',10)
      .text(mode === 'ab' ? 'b*' : 'L*');

    for (const p of patches) {
      const excluded = isOutOfDomain(p, domain);
      const flagged  = !excluded && flaggedSet.has(p.id);
      const [C, M, Y] = p.cmy;
      const inkLevel = (C + M + Y) / 3;
      const cx = mode === 'ab' ? xScale(p.lab[1]) : xScale(inkLevel);
      const cy = mode === 'ab' ? yScale(p.lab[2]) : yScale(p.lab[0]);
      g.append('circle').attr('cx', cx).attr('cy', cy).attr('r', 3.5)
        .attr('fill', excluded ? '#f87171' : flagged ? '#fb923c' : '#4ade80')
        .attr('fill-opacity', 0.75)
        .attr('stroke', '#0f172a').attr('stroke-width', 0.5);
    }

    // Legend
    const leg = g.append('g').attr('transform', `translate(${iw - 110}, 4)`);
    ([['#4ade80','In domain'],['#fb923c','Anomalous'],['#f87171','Excluded']] as [string,string][])
      .forEach(([c,l],i) => {
        leg.append('circle').attr('cx', 5).attr('cy', i*14+5).attr('r', 4)
          .attr('fill', c).attr('fill-opacity', 0.8);
        leg.append('text').attr('x', 14).attr('y', i*14+9)
          .attr('fill','#94a3b8').attr('font-size', 9).text(l);
      });
  }, [patches, domain, mode, flaggedSet]);

  return (
    <div>
      <div className="flex gap-2 mb-2">
        {(['ab','Lv'] as ScatterMode[]).map(m => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-2 py-0.5 rounded text-xs font-mono ${mode===m ? 'bg-slate-600 text-slate-100' : 'text-slate-400 hover:text-slate-200'}`}>
            {m === 'ab' ? 'a* vs b*' : 'ink vs L*'}
          </button>
        ))}
      </div>
      <svg ref={ref} viewBox="0 -60 480 360" className="w-full" />
    </div>
  );
}

// =============================================================================
// DomainView
// =============================================================================

export default function DomainView() {
  const diagnostics = useAppStore(s => s.session.diagnostics);
  const patches     = useAppStore(s => s.session.patches);
  const setDomain   = useAppStore(s => s.setDomain);
  const setView     = useAppStore(s => s.setView);
  const storedDomain = useAppStore(s => s.session.domain);

  // Local slider state (in CMY ink units)
  const [dom, setDom] = useState<WorkingDomain>(
    () => storedDomain ?? diagnostics?.recommendedDomain ?? { ...DEFAULT_WORKING_DOMAIN },
  );

  const set = useCallback((key: keyof WorkingDomain, value: number) => {
    setDom(d => ({ ...d, [key]: value, userOverride: true }));
  }, []);

  // Live exclusion stats
  const excludedCount = patches
    ? patches.filter(p => isOutOfDomain(p, dom)).length
    : 0;
  const totalCount = patches?.length ?? 0;
  const safePct = totalCount > 0
    ? Math.round((totalCount - excludedCount) / totalCount * 100)
    : 0;
  const flaggedCount = diagnostics?.flaggedPatchIds.length ?? 0;

  const handleAutoSuggest = () => {
    if (!diagnostics?.recommendedDomain) return;
    setDom({ ...diagnostics.recommendedDomain, userOverride: false });
  };

  const handleBuild = () => {
    if (!patches) return;
    setDomain(dom);
    setView('build');
  };

  if (!patches || !diagnostics) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 font-mono text-sm">
        Run diagnostics first.
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">

      {/* Left panel — CMY sliders */}
      <div className="w-[400px] shrink-0 border-r border-slate-700 flex flex-col overflow-y-auto">
        <div className="p-5 space-y-5 flex-1">
          <div>
            <h1 className="text-lg font-semibold text-slate-100">Working Domain Ω</h1>
            <p className="text-xs text-slate-400 mt-0.5">
              Ink limits in CMY units (0 = no ink, 1 = full ink).
            </p>
          </div>

          {/* Primary limits */}
          <div className="space-y-2">
            <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Primary ink limits</p>
            <SliderRow label="C max (cyan)"    value={dom.cMax} min={0} max={1} step={0.01}
              rgbMin={rgbMinFromInkMax(dom.cMax)} onChange={v => set('cMax', v)} />
            <SliderRow label="M max (magenta)" value={dom.mMax} min={0} max={1} step={0.01}
              rgbMin={rgbMinFromInkMax(dom.mMax)} onChange={v => set('mMax', v)} />
            <SliderRow label="Y max (yellow)"  value={dom.yMax} min={0} max={1} step={0.01}
              rgbMin={rgbMinFromInkMax(dom.yMax)} onChange={v => set('yMax', v)} />
          </div>

          {/* Binary overprint limits */}
          <div className="space-y-2">
            <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Binary overprint limits</p>
            <p className="text-xs text-slate-600">Excluded when BOTH channels exceed limit — e.g. C &gt; cmMax AND M &gt; cmMax</p>
            <SliderRow label="CM max" value={dom.cmMax} min={0} max={1} step={0.01}
              onChange={v => set('cmMax', v)} />
            <SliderRow label="CY max" value={dom.cyMax} min={0} max={1} step={0.01}
              onChange={v => set('cyMax', v)} />
            <SliderRow label="MY max" value={dom.myMax} min={0} max={1} step={0.01}
              onChange={v => set('myMax', v)} />
          </div>

          {/* CMY overprint limit */}
          <div className="space-y-2">
            <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">CMY overprint limit</p>
            <p className="text-xs text-slate-600">Excluded when ALL three channels exceed limit — C, M, Y all &gt; cmyMax</p>
            <SliderRow label="CMY total" value={dom.cmyMax} min={0} max={1} step={0.01}
              onChange={v => set('cmyMax', v)} />
          </div>

          {/* Stats */}
          <div className="rounded-lg bg-slate-800/60 border border-slate-700 px-3 py-2.5 space-y-1.5 text-sm">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Excluded</span>
              <span className="font-mono text-red-400">{excludedCount} / {totalCount}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Coverage</span>
              <span className={`font-mono ${safePct > 90 ? 'text-emerald-400' : 'text-amber-400'}`}>{safePct}%</span>
            </div>
            {flaggedCount > 0 && (
              <div className="text-amber-400 text-xs">
                ⚠ {flaggedCount} anomalous patch{flaggedCount > 1 ? 'es' : ''} inside domain
              </div>
            )}
          </div>
        </div>

        <div className="p-5 border-t border-slate-700 space-y-2">
          <button onClick={handleAutoSuggest}
            className="w-full px-3 py-2 rounded-lg border border-slate-600
                       text-sm text-slate-300 hover:bg-slate-700/50 transition-colors">
            ↺ Auto-suggest from diagnostics
          </button>
          <button onClick={handleBuild}
            className="w-full px-3 py-2 rounded-lg bg-sky-500 hover:bg-sky-400
                       text-slate-900 font-semibold text-sm transition-colors">
            → Build 3D LUT
          </button>
        </div>
      </div>

      {/* Right panel — scatter */}
      <div className="flex-1 p-5 overflow-auto">
        <h2 className="text-sm font-medium text-slate-300 mb-3">Patch distribution</h2>
        <DomainScatter domain={dom} />
      </div>
    </div>
  );
}
